import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfPen,
  PdfColor,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Grafomotorika (Graphomotor exercises) - Tracing paths between images
 *
 * Layout: 3 portrait pages with fixed row count per page.
 * Each row: left image, dotted line pattern, right image.
 * Start/end points marked with red dots.
 *
 * 30 line patterns across 3 pages (all patterns satisfy f(0) = f(1) = 0):
 * - Page 1: Original patterns (horizontal, wave, quick wave, zigzags, sawtooth)
 * - Page 2: Wavy patterns (various frequencies, inverted, combined)
 * - Page 3: Curved & advanced (loops, figure-8, bounces, ripples)
 *
 * Migrated from C# EduFrame.Img10.Generators.Grafomotorika
 */
export class Grafomotorika implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 4;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const count = Math.min(opts.list.length, 10);
    const { origin, size } = calcOrigin(1, count, margin, PdfSize.A4);
    const pointSize = PdfUnit.FromMillimeters(5);
    const graphSize = size.height * 0.45;

    const leftTexts = opts.list.map((t) => t.left.getText() ?? '');
    const rightTexts = opts.list.map((t) => t.right.getText() ?? '');
    const allTexts = [...leftTexts.map((t) => [t]), ...rightTexts.map((t) => [t])];

    const linePen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
    linePen.dashArray = [1, 5, 1, 5];

    // Page 1: Original patterns (indices 0-9) - f(0) = f(1) = 0 ✓
    const page1Patterns: ((x: number) => number)[] = [
      (x) => 0, // 0: Horizontal line
      (x) => Math.sin(x * Math.PI), // 1: Wave (1 cycle)
      (x) => Math.sin(x * Math.PI * 10), // 2: Quick wave (10 cycles)
      (x) => -Math.abs(Math.sin(x * Math.PI * 2)), // 3: Zigzag downward (2 points)
      (x) => -Math.abs(Math.sin(x * Math.PI * 3)), // 4: Zigzag downward (3 points)
      (x) => -Math.abs(Math.sin(x * Math.PI * 4)), // 5: Zigzag downward (4 points)
      (x) => Math.abs(Math.sin(x * Math.PI * 2)), // 6: Zigzag upward (2 points)
      (x) => Math.abs(Math.sin(x * Math.PI * 3)), // 7: Zigzag upward (3 points)
      (x) => Math.abs(Math.sin(x * Math.PI * 4)), // 8: Zigzag upward (4 points)
      (x) => 10 * x - Math.floor(x * 10), // 9: Sawtooth pattern
    ];

    // Page 2: Challenging waves (indices 10-19) - all f(0) = f(1) = 0 ✓
    const page2Patterns: ((x: number) => number)[] = [
      (x) => Math.sin(x * Math.PI * 6), // 10: Wave (6 cycles)
      (x) => Math.sin(x * Math.PI * 7), // 11: Wave (7 cycles)
      (x) => Math.sin(x * Math.PI * 8), // 12: Wave (8 cycles)
      (x) => Math.sin(x * Math.PI * 9), // 13: Wave (9 cycles)
      (x) => Math.sin(x * Math.PI * x * 10), // 14: Chirp (increasing frequency)
      (x) => Math.sin(x * Math.PI * (1 + x * 5)), // 15: Chirp signal
      (x) => Math.sin(x * Math.PI * 2) * Math.sin(x * Math.PI * 3), // 16: Beat pattern
      (x) => Math.sin(x * Math.PI * 4) * (1 - x), // 17: Damped wave
      (x) => Math.sin(x * Math.PI) * Math.sin(x * Math.PI * 5), // 18: Complex interference
      (x) => Math.sin(x * Math.PI * 3) * Math.cos(x * Math.PI * 2), // 19: Phase shift
    ];

    // Page 3: Very challenging (indices 20-29) - multi-scale, cusps, envelopes - all f(0) = f(1) = 0 ✓
    const page3Patterns: ((x: number) => number)[] = [
      (x) => Math.sin(x * Math.PI * 10) * 0.3 + Math.sin(x * Math.PI * 3) * 0.7, // 20: Multi-scale (large + small)
      (x) => Math.sin(x * Math.PI * 2) * Math.sin(x * Math.PI * 3) * Math.sin(x * Math.PI * 5), // 21: Triple product
      (x) => Math.abs(Math.sin(x * Math.PI * 5)) * Math.sin(x * Math.PI), // 22: Cusp pattern
      (x) => Math.sin(x * Math.PI * 4) * (1 - Math.abs(2 * x - 1)), // 23: Envelope with sharp points
      (x) => Math.sin(x * Math.PI * x * 15), // 24: Exponential frequency
      (x) =>
        Math.sin(x * Math.PI * 2) * Math.cos(x * Math.PI * 3) + Math.sin(x * Math.PI * 7) * 0.3, // 25: Complex interference
      (x) => Math.sin(x * Math.PI * 6) * Math.sin(x * Math.PI * 7), // 26: High-frequency beat
      (x) => Math.sin(x * Math.PI) * Math.sin(x * Math.PI * 2) * Math.sin(x * Math.PI * 3), // 27: Triple product
      (x) => Math.sin(x * Math.PI * 8) * (1 - x) * x * 4, // 28: Parabolic envelope
      (x) =>
        Math.sin(x * Math.PI * 3) * Math.cos(x * Math.PI * 4) + Math.sin(x * Math.PI * 7) * 0.2, // 29: Multi-frequency
    ];

    const totalPages = 3;
    const pagePatterns = [page1Patterns, page2Patterns, page3Patterns];

    for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
      const page = pdf.addPage();
      const cellSize = new PdfSize(size.height, size.height);
      const labelFont = page.adjustFontMulti(allTexts, GUtil.baseFont, cellSize);
      const patterns = pagePatterns[pageIdx];

      for (let row = 0; row < count; row++) {
        const rect = new PdfRect(origin.x, origin.y + row * size.height, size.width, size.height);
        const imgRect = new PdfRect(rect.x, rect.y, rect.height, rect.height);
        const labelRect = new PdfRect(rect.right - imgRect.width, rect.y, rect.height, rect.height);

        const p1 = new PdfPoint(imgRect.right + pointSize, imgRect.y + imgRect.height / 2);
        const p2 = new PdfPoint(labelRect.x - pointSize, imgRect.y + imgRect.height / 2);

        const patternFn = patterns[row];
        const points: PdfPoint[] = [];
        for (let x1 = 0; x1 <= 100; x1++) {
          const y = patternFn(x1 / 100.0);
          points.push(new PdfPoint(p1.x + ((p2.x - p1.x) / 100) * x1, p1.y - y * graphSize));
        }
        page.drawLines(linePen, points);

        const r1 = new PdfRect(p1.x - pointSize / 2, p1.y - pointSize / 2, pointSize, pointSize);
        const r2 = new PdfRect(p2.x - pointSize / 2, p2.y - pointSize / 2, pointSize, pointSize);
        page.drawEllipse(null, PdfBrush.Red, r1);
        page.drawEllipse(null, PdfBrush.Red, r2);

        page.drawLabel(opts.list[row].left, { rect: imgRect, font: labelFont });
        page.drawLabel(opts.list[row].right, { rect: labelRect, font: labelFont });
        page.drawRectangle(GUtil.basePen, null, labelRect);
      }
    }
  }
}
